import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Mail, Settings } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'
import { CircleColorPicker } from '@/components/CircleColor'
import { CircleName } from '@/components/CircleName'
import { FormError } from '@/components/FormError'
import { Loader } from '@/components/Loader'
import { InviteForm } from '@/components/people/InviteForm'
import { Avatar } from '@/components/tree/Avatar'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { api, type Me } from '@/lib/api'
import { useFamily, type Family } from '@/lib/families'
import { firstName } from '@/lib/tree'

type Addable = { id: string; name: string; portrait: string | null; waiting: boolean }

// A circle's quick edit, from its row in your profile: its name and colour, who's in it, and bringing more people in,
// from the people you already know or with an invite. A sheet from the bottom on a phone. Adding someone sends them
// an invite they accept; nobody is put in a circle without saying yes. Your own space only has its colour.
export function CircleQuickEdit({ circleId, me, onClose }: { circleId: string | null; me: Me; onClose: () => void }) {
  const family = useFamily(circleId ?? '', { enabled: !!circleId })
  return (
    <Dialog open={!!circleId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-lg" aria-describedby={undefined}>
        {family.data && family.data.id === circleId ? (
          <QuickEdit key={family.data.id} family={family.data} me={me} onClose={onClose} />
        ) : (
          <>
            <DialogTitle className="sr-only">Circle</DialogTitle>
            <FormError error={family.error} />
            {!family.error && <Loader />}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

function QuickEdit({ family, me, onClose }: { family: Family; me: Me; onClose: () => void }) {
  const [inviting, setInviting] = useState(false)
  if (inviting)
    return (
      <>
        <DialogHeader>
          <DialogTitle>Invite someone</DialogTitle>
          <DialogDescription>
            One invite for one person, to join {family.name}. It works once, for 7 days.
          </DialogDescription>
        </DialogHeader>
        <InviteForm family={family} />
        <Button variant="outline" className="self-start" onClick={() => setInviting(false)}>
          Back to {family.name}
        </Button>
      </>
    )
  return (
    <>
      <DialogHeader>
        <DialogTitle>{family.personal ? family.name : `Edit ${family.name}`}</DialogTitle>
      </DialogHeader>
      <div className="flex flex-col gap-6">
        {!family.personal && <CircleName family={family} id="quick-rename" />}
        <CircleColorPicker family={family} name="quick-circle-color" />
        {!family.personal && <WhosIn family={family} me={me} />}
        {!family.personal && me.canInvite && <AddPeople family={family} onInvite={() => setInviting(true)} />}
        <Link
          to={`/circle/${family.id}/settings`}
          onClick={onClose}
          className="flex items-center gap-2 self-start rounded-md text-sm text-brand underline underline-offset-4 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
        >
          <Settings className="size-4" aria-hidden /> All circle settings
        </Link>
      </div>
    </>
  )
}

// Everyone who's joined, and the invites still waiting. Whoever made the circle, or invited someone, can take them
// back out, after saying so.
function WhosIn({ family, me }: { family: Family; me: Me }) {
  const queryClient = useQueryClient()
  const remove = useMutation({
    mutationFn: (userId: string) => api(`/families/${family.id}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['family', family.id] })
      await queryClient.invalidateQueries({ queryKey: ['circles'] })
      await queryClient.invalidateQueries({ queryKey: ['addable', family.id] })
      await queryClient.invalidateQueries({ queryKey: ['my-people'] })
    },
  })
  const [now] = useState(Date.now)
  const waiting = family.invites.filter((x) => !x.declined && x.expiresAt > now)
  return (
    <section aria-labelledby="quick-people" className="flex flex-col gap-1">
      <h3 id="quick-people" className="text-sm font-medium">
        People in {family.name} <span className="font-normal text-muted-foreground">{family.members.length}</span>
      </h3>
      <ul className="flex flex-col">
        {family.members.map((x) => (
          <li key={x.id} className="flex min-h-14 items-center gap-3 py-1.5">
            <Avatar person={x} className="size-10 text-base" />
            <span className="min-w-0 flex-1 truncate">{x.id === me.id ? `${x.name} (you)` : x.name}</span>
            {x.canRemove && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="sm" disabled={remove.isPending} aria-label={`Remove ${x.name}`}>
                    Remove
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Take {firstName(x.name)} out of {family.name}?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      They’ll stop seeing what’s shared here. What they added stays with the circle, and you can invite
                      them back any time.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Keep them</AlertDialogCancel>
                    <AlertDialogAction onClick={() => remove.mutate(x.id)}>Remove</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </li>
        ))}
        {waiting.map((x) => (
          <li key={x.id} className="flex min-h-14 items-center gap-3 py-1.5 text-muted-foreground">
            <span aria-hidden className="grid size-10 shrink-0 place-items-center rounded-full border border-dashed">
              <Mail className="size-4" />
            </span>
            <span className="min-w-0 flex-1 truncate">{x.name ?? x.person ?? x.email}</span>
            <span className="text-sub">Invited</span>
          </li>
        ))}
      </ul>
      <FormError error={remove.error} />
    </section>
  )
}

// The people you know who aren't in the circle yet, each a tap from an invite, and inviting anyone else.
function AddPeople({ family, onInvite }: { family: Family; onInvite: () => void }) {
  const queryClient = useQueryClient()
  const addable = useQuery({
    queryKey: ['addable', family.id],
    queryFn: async () => (await api<{ people: Addable[] }>(`/families/${family.id}/addable`)).people,
  })
  const invite = useMutation({
    mutationFn: (userId: string) =>
      api(`/families/${family.id}/invites`, { method: 'POST', body: { via: 'account', userId } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['addable', family.id] })
      await queryClient.invalidateQueries({ queryKey: ['family', family.id] })
    },
  })
  // Someone already invited is listed with the circle's people, as Invited.
  const people = (addable.data ?? []).filter((x) => !x.waiting)
  return (
    <section aria-labelledby="quick-add" className="flex flex-col gap-1">
      <h3 id="quick-add" className="text-sm font-medium">
        Add from your connections
      </h3>
      {people.length > 0 ? (
        <ul className="flex flex-col">
          {people.map((x) => (
            <li key={x.id} className="flex min-h-14 items-center gap-3 py-1.5">
              <Avatar person={x} className="size-10 text-base" />
              <span className="min-w-0 flex-1 truncate">{x.name}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={invite.isPending}
                aria-label={`Invite ${x.name}`}
                onClick={() => invite.mutate(x.id)}
              >
                Invite
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        addable.isSuccess && (
          <p className="text-sub text-muted-foreground">Everyone you’re connected to is here or invited.</p>
        )
      )}
      <FormError error={addable.error ?? invite.error} />
      <Button variant="outline" className="mt-2 self-start" onClick={onInvite}>
        <Mail aria-hidden /> Invite someone new
      </Button>
    </section>
  )
}
